import * as React from "react"
import { Landmark, PiggyBank, ReceiptText, TrendingUp, Wallet } from "lucide-react"

import { SummaryGrid } from "@/features/dashboard/components/SummaryGrid"
import { useCarteraData } from "../hooks/useCarteraData"
import { useCarteraDialogs } from "../hooks/useCarteraDialogs"
import { CarteraFab } from "../components/atoms/CarteraFab"
import { CarteraHero } from "../components/molecules/CarteraHero"
import { CollectionSection } from "../components/molecules/CollectionSection"
import { AccountCollection } from "../components/organisms/AccountCollection"
import { IncomeCollection } from "../components/organisms/IncomeCollection"
import { MovementCollection } from "../components/organisms/MovementCollection"
import { CarteraDialogs } from "../components/organisms/CarteraDialogs"

export function CarteraPage() {
  const [amountsVisible, setAmountsVisible] = React.useState(true)

  // Fetch all transactions so each collection table can filter independently
  const {
    accounts,
    transactionsByType,
    accountsById,
    categoriesById,
    availableMonths,
    isLoadingTransactions,
    isFetchingTransactions,
  } = useCarteraData()

  const monthOptions = React.useMemo(() => {
    const options = [{ value: "ALL", label: "Todos los meses" }]
    if (availableMonths && availableMonths.length > 0) {
      for (const m of availableMonths) {
        options.push({ value: m.value, label: m.label })
      }
    }
    return options
  }, [availableMonths])

  const {
    transactionType,
    isTransactionDialogOpen,
    editingTransaction,
    deletingTransaction,
    isAccountDialogOpen,
    editingAccount,
    deletingAccount,
    openTransactionDialog,
    openEditTransactionDialog,
    closeTransactionDialog,
    openDeleteTransactionDialog,
    closeDeleteTransactionDialog,
    openAccountDialog,
    openEditAccountDialog,
    closeAccountDialog,
    openDeleteAccountDialog,
    closeDeleteAccountDialog,
  } = useCarteraDialogs()

  const handleToggleAmounts = React.useCallback(() => {
    setAmountsVisible((visible) => !visible)
  }, [])

  const handleOpenExpenseDialog = React.useCallback(() => {
    openTransactionDialog("EXPENSE")
  }, [openTransactionDialog])

  const handleOpenIncomeDialog = React.useCallback(() => {
    openTransactionDialog("INCOME")
  }, [openTransactionDialog])

  const handleOpenSavingDialog = React.useCallback(() => {
    openTransactionDialog("SAVING")
  }, [openTransactionDialog])

  return (
    <div className="cartera-root flex flex-col gap-6 sm:gap-8 pb-16 sm:pb-0" data-view="compact">
      {/* Hero Header */}
      <CarteraHero
        amountsVisible={amountsVisible}
        onToggleAmounts={handleToggleAmounts}
        onOpenAccountDialog={openAccountDialog}
        onOpenTransactionDialog={handleOpenExpenseDialog}
      />

      {/* Summary KPI Grid (Collapsible, default closed) */}
      <CollectionSection
        title="Resumen financiero"
        description="Balances disponibles, ahorros acumulados y balance del periodo."
        icon={Wallet}
        defaultOpen={false}
        contentClassName="p-3.5 sm:p-4 bg-muted/10"
      >
        <SummaryGrid amountsVisible={amountsVisible} />
      </CollectionSection>

      {/* Collections Section */}
      <div className="flex flex-col gap-4 sm:gap-5">
        <CollectionSection
          title="Cuentas"
          description="Estructura de cuentas y saldo actual por cuenta."
          count={accounts.length}
          icon={Landmark}
          actionLabel="Nueva cuenta"
          onAction={openAccountDialog}
        >
          <AccountCollection 
            accounts={accounts} 
            amountsVisible={amountsVisible}
            onEdit={openEditAccountDialog}
            onDelete={openDeleteAccountDialog}
          />
        </CollectionSection>

        <CollectionSection
          title="Ingresos"
          description="Registro histórico de entradas e importes por cuenta."
          count={transactionsByType.INCOME.length}
          icon={TrendingUp}
          actionLabel="Nuevo ingreso"
          onAction={handleOpenIncomeDialog}
          isFetching={isFetchingTransactions}
          defaultOpen={false}
        >
          <IncomeCollection
            transactions={transactionsByType.INCOME}
            accounts={accountsById}
            categories={categoriesById}
            amountsVisible={amountsVisible}
            availableMonths={monthOptions}
            isLoading={isLoadingTransactions}
            isFetching={isFetchingTransactions}
            onEdit={openEditTransactionDialog}
            onDelete={openDeleteTransactionDialog}
          />
        </CollectionSection>

        <CollectionSection
          title="Gastos"
          description="Registro detallado de salidas con importes y categorías."
          count={transactionsByType.EXPENSE.length}
          icon={ReceiptText}
          actionLabel="Nuevo gasto"
          isFetching={isFetchingTransactions}
          onAction={handleOpenExpenseDialog}
        >
          <MovementCollection
            transactions={transactionsByType.EXPENSE}
            accounts={accountsById}
            categories={categoriesById}
            availableMonths={monthOptions}
            isLoading={isLoadingTransactions}
            isFetching={isFetchingTransactions}
            amountsVisible={amountsVisible}
            kind="EXPENSE"
            onEdit={openEditTransactionDialog}
            onDelete={openDeleteTransactionDialog}
          />
        </CollectionSection>

        <CollectionSection
          title="Ahorros"
          description="Aportaciones registradas para el crecimiento de tu capital."
          count={transactionsByType.SAVING.length}
          icon={PiggyBank}
          actionLabel="Nuevo ahorro"
          isFetching={isFetchingTransactions}
          onAction={handleOpenSavingDialog}
          defaultOpen={false}
        >
          <MovementCollection
            transactions={transactionsByType.SAVING}
            accounts={accountsById}
            categories={categoriesById}
            availableMonths={monthOptions}
            isLoading={isLoadingTransactions}
            isFetching={isFetchingTransactions}
            amountsVisible={amountsVisible}
            kind="SAVING"
            onEdit={openEditTransactionDialog}
            onDelete={openDeleteTransactionDialog}
          />
        </CollectionSection>
      </div>

      {/* Mobile Floating Action Button */}
      <CarteraFab onClick={handleOpenExpenseDialog} />

      {/* Modals & Dialogs */}
      <CarteraDialogs
        accounts={accounts}
        accountsById={accountsById}
        categoriesById={categoriesById}
        transactionType={transactionType}
        isTransactionDialogOpen={isTransactionDialogOpen}
        editingTransaction={editingTransaction}
        deletingTransaction={deletingTransaction}
        isAccountDialogOpen={isAccountDialogOpen}
        editingAccount={editingAccount}
        deletingAccount={deletingAccount}
        onCloseTransactionDialog={closeTransactionDialog}
        onCloseDeleteDialog={closeDeleteTransactionDialog}
        onCloseAccountDialog={closeAccountDialog}
        onCloseDeleteAccountDialog={closeDeleteAccountDialog}
      />
    </div>
  )
}

export default CarteraPage
